// @flow
/* eslint-disable no-shadow */
import padCharsStart from 'lodash/fp/padCharsStart';
import identity from 'lodash/fp/identity';
import { parseISO } from 'date-fns';
import BluebirdPromise from 'bluebird';
import flatMap from 'lodash/fp/flatMap';
import uniqBy from 'lodash/fp/uniqBy';
import sortBy from 'lodash/fp/sortBy';

import { zipArchive, downloadFile } from './file';
import { retrieveFilesFromUrls } from './api/quik-order-history';
import type { DefaultOptions } from '../ui/pages/quik-order-history/orders';

import type { ImageTypeBucket, ImageRecord } from 'modules/images';
import { toDate, localDateString } from 'modules/date';
import { catalogsForCustomer, type User } from 'modules/user';
import type { PriceGroupToMonetaryAmountMap } from 'modules/monetary-amount';

type QuikBrand = {
  BRAND: string,
};

export const quikDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = padCharsStart('0')(2)((date.getMonth() + 1).toString());
  const day = padCharsStart('0')(2)(date.getDate().toString());
  return `${year}${month}${day}`;
};

// Quik date come in as YYYYMMDD, need to covert it to string
// toDate case use, to be used within localDateString func
export const quikDateToLocalDate = (
  str: string,
  locale: string,
  options?: Intl$DateTimeFormatOptions,
): string => {
  const isQuikDateStr =
    str.length === 8 && !Number.isNaN(Number(str)) && str.indexOf('-') === -1;
  if (isQuikDateStr) {
    return localDateString(
      `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`,
      locale,
      options,
    );
  }
  return localDateString(str, locale, options);
};

const maybeQuikDate = (date?: Date | null): string =>
  date ? quikDate(date) : '';

// This is constant across all requests
export const I_LANGUAGE = 'E';

// Print Document
// Use V1 for orders, V3 for Invoices
export type QuikApplKey = 'V1' | 'V3';

export type QuikPrintDocumentQuery = {
  documentNumbers: $ReadOnlyArray<string>,
  customerNumber: string,
  applKey?: QuikApplKey,
};

export type QuikDownloadPackingListQuery = {
  deliveryNumber: string,
  applKey?: QuikApplKey,
  multiple?: boolean,
};

export type QuikDownloadUpcQuery = {
  customerNumber: string,
  documentNumbers?: $ReadOnlyArray<string>,
  lineItem?: string[],
  shipmentItem?: string[],
};

type QuikMetaReturn = {
  TYPE: string,
  ID: string,
  NUMBER: string,
  MESSAGE: string,
  LOG_NO: string,
  LOG_MSG_NO: string,
  MESSAGE_V1: string,
  MESSAGE_V2: string,
  MESSAGE_V3: string,
  MESSAGE_V4: string,
  PARAMETER: string,
  ROW: string,
  FIELD: string,
  SYSTEM: string,
};

// Orders (includes Returns)
export type SerializedQuikOrder = {
  BRANDS: QuikBrand[],
  CANCEL_DATE: string,
  CREATE_DATE: string,
  CREATED_BY_NAME: string,
  CUSTOMER_PO_NUMBER: string,
  DOCUMENT_CURRENCY: string,
  ORDER_CONTEXT: string,
  ORDER_NUMBER: string,
  ORDER_REASON: string,
  ORDER_SOURCE: string,
  ORDER_STATUS: string,
  ORDER_TYPE_TEXT: string,
  ORDER_TYPE: string,
  REQUESTED_DELIVERY_DATE: string,
  SHIP_TO_NAME: string,
  TOTAL_VALUE: string,
};

export type QuikOrder = $ReadOnly<{|
  id: string,
  brands: QuikBrand[],
  cancelDate: string,
  createDate: string,
  createdByName: string,
  customerPoNumber: string,
  documentCurrency: string,
  orderContext: string,
  orderNumber: string,
  orderReason: string,
  orderSource: string,
  orderStatus: string,
  orderTypeText: string,
  orderType: string,
  requestedDeliveryDate: string,
  shipToName: string,
  totalValue: string,
|}>;

type QuikOrderListResponse = {
  ET_ORDER_LIST: SerializedQuikOrder[],
  ET_RETURN: QuikMetaReturn[],
};

export type SerializedQuikPendingOrderListResponse = {
  _id: string,
  author_id: string,
  catalog_key: string,
  copied_to: ?string,
  created_at: string,
  customer: string,
  duplicated_for: string[],
  duplicated_from_id: ?string,
  erp_order_numbers: string[],
  name: string,
  number: string,
  /* 🤔 what is payment type? */
  payment: any,
  shared_with_id: ?string,
  sharer_id: ?string,
  state: 'final_submission',
  submission: {
    _id: string,
    created_at: string,
    state: 'sent',
    updated_at: string,
  },
  total_prices: PriceGroupToMonetaryAmountMap,
  total_quantity: number,
  updated_at: string,
  catalog_name: string,
  customer_name: string,
  po_numbers: string[],
  start_date: string,
};

export type QuikPendingOrder = $ReadOnly<{|
  id: string,
  createdOn: Date,
  startDate: Date,
  name: string,
  orderNumber: string,
  totalValue: PriceGroupToMonetaryAmountMap,
  poNumbers: string[],
|}>;

export type SerializedQuikOrderListQuery = {
  I_LANGUAGE: string,
  I_SOLD_TO_NUMBER: string,
  I_SALES_REP: string,
  I_CUSTOMER_PO_NUMBER: string,
  I_ORDER_NUMBER: string,
  I_SHIP_TO_NUMBER: string,
  I_REQUESTED_DELIVERY_DATE_FROM: string,
  I_REQUESTED_DELIVERY_DATE_TO: string,
  I_ORDER_TYPE: string,
  I_ORDER_CREATE_DATE_FROM: string,
  I_ORDER_CREATE_DATE_TO: string,
  I_ORDER_STATUS: string,
  I_DELIVERY_NUMBER: string,
  I_SHIPPED_DATE_FROM: string,
  I_SHIPPED_DATE_TO: string,
  IT_SHIP_TOS: string[],
  I_EMAIL_ID: string,
  I_MATERIAL: string,
  I_SEASON: string,
  IT_BRANDS: { SPART: mixed }[],
  I_B2B_ORDER_NO: string,
};

export type SerializedQuikPendingOrderListQuery = {
  customerNumber: string,
  customerPoNumber: string,
  orderNumber?: string,
  createdFrom?: Date | null,
  createdTo?: Date | null,
  startFrom?: Date,
  startTo?: Date,
};

export type QuikOrderListQuery = {
  customerPoNumber?: string,
  orderNumber?: string,
  b2bOrderNumber?: string,
  soldToNumber: string,
  shipToNumber: string,
  salesRep?: string,
  requestedDeliveryDateFrom?: Date | null,
  requestedDeliveryDateTo?: Date | null,
  orderType?: string,
  orderCreateDateFrom?: Date | null,
  orderCreateDateTo?: Date | null,
  orderStatus?: string,
  deliveryNumber?: string,
  orderCancelDateFrom?: Date,
  orderCancelDateTo?: Date,
  shipTos?: string[],
  emailId?: string,
  material?: string,
  season?: string,
  brand: $ReadOnlyArray<{| label: string, value: string |}>,
  sellingGroup: $ReadOnlyArray<{| label: string, value: string |}>,
};

export const serializedQuikOrderListQuery = ({
  soldToNumber = '',
  salesRep = '',
  customerPoNumber = '',
  orderNumber = '',
  shipToNumber = '',
  requestedDeliveryDateFrom = null,
  requestedDeliveryDateTo = null,
  orderType = '',
  orderCreateDateFrom = null,
  orderCreateDateTo = null,
  orderStatus = '',
  deliveryNumber = '',
  orderCancelDateFrom,
  orderCancelDateTo,
  shipTos = [],
  emailId = '',
  material = '',
  season = '',
  brand = [],
  sellingGroup = [],
  b2bOrderNumber = '',
}: QuikOrderListQuery): SerializedQuikOrderListQuery =>
  // This sort matters as their parser is quite brittle
  ({
    I_LANGUAGE,
    I_SOLD_TO_NUMBER: soldToNumber,
    I_SALES_REP: salesRep,
    I_CUSTOMER_PO_NUMBER: customerPoNumber,
    I_ORDER_NUMBER: orderNumber,
    I_SHIP_TO_NUMBER: shipToNumber,
    I_REQUESTED_DELIVERY_DATE_FROM: maybeQuikDate(requestedDeliveryDateFrom),
    I_REQUESTED_DELIVERY_DATE_TO: maybeQuikDate(requestedDeliveryDateTo),
    I_ORDER_TYPE: orderType,
    I_ORDER_CREATE_DATE_FROM: maybeQuikDate(orderCreateDateFrom),
    I_ORDER_CREATE_DATE_TO: maybeQuikDate(orderCreateDateTo),
    I_ORDER_STATUS: orderStatus,
    I_DELIVERY_NUMBER: deliveryNumber,
    I_SHIPPED_DATE_FROM: maybeQuikDate(orderCancelDateFrom),
    I_SHIPPED_DATE_TO: maybeQuikDate(orderCancelDateTo),
    IT_SHIP_TOS: shipTos,
    I_EMAIL_ID: emailId,
    I_MATERIAL: material,
    I_SEASON: season,
    IT_BRANDS: Array.isArray(brand)
      ? brand.map((b) => ({ SPART: b.value }))
      : [{ SPART: brand.value }],
    I_B2B_ORDER_NO: b2bOrderNumber,
    IT_SELLING_GROUPS: Array.isArray(sellingGroup)
      ? sellingGroup.map((s) => ({ SELLING_GROUP: s.value }))
      : [{ SELLING_GROUP: sellingGroup.value }],
  });

export const deserializedQuikOrderListResponse = (
  response: QuikOrderListResponse,
): Array<any> => {
  // TODO: this pattern is totally broken and messing with the typing below,
  // which is why the FlowFixMe is necessary. make this stuff make sense, and
  // the type error will dry up.
  const { ET_ORDER_LIST: orderList = [] } = response || { ...null };
  return orderList.map(
    ({
      BRANDS,
      CANCEL_DATE,
      CREATE_DATE,
      CREATED_BY_NAME,
      CUSTOMER_PO_NUMBER,
      DOCUMENT_CURRENCY,
      ORDER_CONTEXT,
      ORDER_NUMBER,
      ORDER_REASON,
      ORDER_SOURCE,
      ORDER_STATUS,
      ORDER_TYPE_TEXT,
      ORDER_TYPE,
      REQUESTED_DELIVERY_DATE,
      SHIP_TO_NAME,
      TOTAL_VALUE,
    }: SerializedQuikOrder): QuikOrder =>
      ({
        id: `QuikOrder:${[ORDER_NUMBER, CUSTOMER_PO_NUMBER, CREATE_DATE].join(
          '::',
        )}`,
        brands: BRANDS,
        cancelDate: CANCEL_DATE,
        createDate: CREATE_DATE,
        createdByName: CREATED_BY_NAME,
        customerPoNumber: CUSTOMER_PO_NUMBER,
        documentCurrency: DOCUMENT_CURRENCY,
        orderContext: ORDER_CONTEXT,
        orderNumber: ORDER_NUMBER,
        orderReason: ORDER_REASON,
        orderSource: ORDER_SOURCE,
        orderStatus: ORDER_STATUS,
        orderTypeText: ORDER_TYPE_TEXT,
        orderType: ORDER_TYPE,
        requestedDeliveryDate: REQUESTED_DELIVERY_DATE,
        shipToName: SHIP_TO_NAME,
        totalValue: TOTAL_VALUE,
      }: QuikOrder),
  );
};

/* eslint-disable camelcase */
export const deserializedQuikPendingOrderListResponse = ({
  _id,
  created_at,
  name,
  number,
  total_prices,
  po_numbers,
  start_date,
}: SerializedQuikPendingOrderListResponse): QuikPendingOrder => ({
  id: _id,
  createdOn: parseISO(created_at),
  startDate: toDate(start_date),
  name,
  orderNumber: number,
  totalValue: total_prices,
  poNumbers: po_numbers,
});
/* eslint-enable camelcase */

export type QuikPendingOrderListQuery = $ReadOnly<{|
  customerNumber: string,
  poNumber: string,
  elasticOrderNumber: string,
  createdFrom: Date | null,
  createdTo: Date | null,
  startFrom: Date,
  startTo: Date,
|}>;

/* 💁 The reason this query isn't formatted like the other ones is that it doesn't involve
the Quik ERP API.
*/
export const serializedQuikPendingOrderListQuery = ({
  customerNumber,
  poNumber,
  elasticOrderNumber,
  createdFrom,
  createdTo,
  startFrom,
  startTo,
}: QuikPendingOrderListQuery): SerializedQuikPendingOrderListQuery => ({
  customerNumber,
  customerPoNumber: poNumber,
  orderNumber: elasticOrderNumber,
  createdFrom,
  createdTo,
  startFrom,
  startTo,
});

// Contacts
export type SerializedQuikContact = {
  BRANDS: QuikBrand[],
  CONTACT_NAME: string,
  CONTACT_NUMBER: string,
  EMAIL: string,
  REP_FUNCTION_NAME: string,
  REP_FUNCTION_VALUE: string,
  TELEPHONE: string,
};

export type QuikContact = $ReadOnly<{|
  id: string,
  brands: QuikBrand[],
  contactName: string,
  contactNumber: string,
  email: string,
  repFunctionName: string,
  repFunctionValue: string,
  telephone: string,
|}>;

type QuikContactListResponse = {
  ET_CONTACT_LIST: SerializedQuikContact[],
  ET_RETURN: QuikMetaReturn[],
};

export type SerializedQuikContactListQuery = {
  I_CUSTOMER_NUMBER: string,
  I_LANGUAGE: string,
};

export type QuikContactListQuery = {
  customerNumber: string,
};

export const serializedQuikContactListQuery = ({
  customerNumber = '',
}: QuikContactListQuery): SerializedQuikContactListQuery => ({
  I_LANGUAGE,
  I_CUSTOMER_NUMBER: customerNumber,
});

export const deserializedQuikContactListResponse = (
  response: QuikContactListResponse,
): Array<any> => {
  // TODO: this pattern is totally broken and messing with the typing below,
  // which is why the FlowFixMe is necessary. make this stuff make sense, and
  // the type error will dry up.
  const { ET_CONTACT_LIST: contactList = [] } = response || { ...null };
  return contactList.map(
    ({
      BRANDS,
      CONTACT_NAME,
      CONTACT_NUMBER,
      EMAIL,
      REP_FUNCTION_NAME,
      REP_FUNCTION_VALUE,
      TELEPHONE,
    }: SerializedQuikContact): QuikContact =>
      ({
        id: CONTACT_NUMBER,
        brands: BRANDS,
        contactName: CONTACT_NAME,
        contactNumber: CONTACT_NUMBER,
        email: EMAIL,
        repFunctionName: REP_FUNCTION_NAME,
        repFunctionValue: REP_FUNCTION_VALUE,
        telephone: TELEPHONE,
      }: QuikContact),
  );
};

// Shipments
export type SerializedQuikShipment = {
  PO_NUMBER: string,
  SHIPMENT_NUMBER: string,
  SHIP_DATE: string,
  CARRIER: string,
  ORDER_NUMBER: string,
  SHIPTO_NAME: string,
  TOTAL_SHIPPED_QTY: string,
  DELIVERY_NUMBER: string,
};

export type QuikShipment = $ReadOnly<{|
  poNumber: string,
  shipmentNumber: string,
  shipDate: string,
  carrier: string,
  orderNumber: string,
  shipToName: string,
  totalShippedQty: number,
  deliveryNumber: string,
|}>;

type QuikShipmentListResponse = {
  ET_OPEN_SHIPMENT_LIST: SerializedQuikShipment[],
  ET_SHIPMENT_LIST: SerializedQuikShipment[],
  ET_RETURN: QuikMetaReturn,
};

export type SerializedQuikShipmentListQuery = {
  I_LANGUAGE: string,
  I_CUSTOMER_NUMBER: string,
  I_PO_NUMBER: string,
  I_ORDER_NUMBER: string,
  I_INVOICE_NUMBER: string,
  I_DELIVERY_NUMBER: string,
  I_SHIPMENT_DATE_FROM: string,
  I_SHIPMENT_DATE_TO: string,
  I_OPEN_SHIPMENTS: string,
  I_DAYS: string,
  I_CREATED_BY_EMAIL: string,
  IT_SHIP_TOS: string[],
};

export type QuikShipmentListQuery = {
  customerNumber: string,
  poNumber?: string,
  orderNumber?: string,
  invoiceNumber?: string,
  deliveryNumber?: string,
  shipmentDateFrom?: Date | null,
  shipmentDateTo?: Date | null,
  openShipments?: string,
  days?: string,
  createdByEmail?: string,
  shipTos?: string[],
};

export const serializedQuikShipmentListQuery = ({
  customerNumber = '',
  poNumber = '',
  orderNumber = '',
  invoiceNumber = '',
  deliveryNumber = '',
  shipmentDateFrom = null,
  shipmentDateTo = null,
  openShipments = '',
  days = '0',
  createdByEmail = '',
  shipTos = [],
}: QuikShipmentListQuery): SerializedQuikShipmentListQuery => ({
  I_LANGUAGE,
  I_CUSTOMER_NUMBER: customerNumber,
  I_PO_NUMBER: poNumber,
  I_ORDER_NUMBER: orderNumber,
  I_INVOICE_NUMBER: invoiceNumber,
  I_DELIVERY_NUMBER: deliveryNumber,
  I_SHIPMENT_DATE_FROM: maybeQuikDate(shipmentDateFrom),
  I_SHIPMENT_DATE_TO: maybeQuikDate(shipmentDateTo),
  I_OPEN_SHIPMENTS: openShipments,
  I_DAYS: days,
  I_CREATED_BY_EMAIL: createdByEmail,
  IT_SHIP_TOS: shipTos,
});

export const deserializedQuikShipmentListResponse = (
  response: QuikShipmentListResponse,
): Array<any> => {
  // TODO: this pattern is totally broken and messing with the typing below,
  // which is why the FlowFixMe is necessary. make this stuff make sense, and
  // the type error will dry up.
  const { ET_SHIPMENT_LIST: shipmentList = [] } = response || { ...null };
  return shipmentList.map(
    ({
      PO_NUMBER,
      SHIPMENT_NUMBER,
      SHIP_DATE,
      CARRIER,
      ORDER_NUMBER,
      SHIPTO_NAME,
      TOTAL_SHIPPED_QTY,
      DELIVERY_NUMBER,
    }: SerializedQuikShipment): QuikShipment =>
      ({
        poNumber: PO_NUMBER,
        shipmentNumber: SHIPMENT_NUMBER,
        shipDate: SHIP_DATE,
        carrier: CARRIER,
        orderNumber: ORDER_NUMBER,
        shipToName: SHIPTO_NAME,
        totalShippedQty: Number(TOTAL_SHIPPED_QTY),
        deliveryNumber: DELIVERY_NUMBER,
      }: QuikShipment),
  );
};

// Shipment Details
export type QuikZSchedule = {
  id: string,
  scheduleLine?: string,
  gridSize: string,
  quantity: number,
  openQuantity?: number,
  allocatedQuantity?: number,
  inProcessQuantity?: number,
  invoicedQuantity?: number,
  rejectedQuantity?: number,
  drawnQuantity?: number,
  remainingQuantity?: number,
  uom: string,
  confirmedDate?: string,
  status?: string,
  shippedQuantity?: number,
  deliveredQuantity?: number,
  upc: string,
};

type SerializedQuikZSchedule = {
  SCHEDULE_LINE?: string,
  GRID_SIZE: string,
  QUANTITY: number,
  OPEN_QUANTITY?: number,
  ALLOCATED_QUANTITY?: number,
  IN_PROCESS_QUANTITY?: number,
  INVOICED_QUANTITY?: number,
  REJECTED_QUANTITY?: number,
  DRAWN_QUANTITY?: number,
  REMAINING_QUANTITY?: number,
  UOM: string,
  CONFIRMED_DATE?: string,
  STATUS?: string,
  SHIPPED_QUANTITY?: number,
  DELIVERED_QUANTITY?: number,
  UPC: string,
};

type SerializedQuikShipmentDetail = {
  CARTON_NUMBER: string,
  MATERIAL_NUMBER: string,
  QUANTITY: number,
  UOM: string,
  MATERIAL_DESCRIPTION: string,
  CARRIER: string,
  TRACKING_NUMBER: string,
  TRACKING_URL: string,
  ORDER_NUMBER: string,
  ORDER_ITEM: string,
  OLD_MATERIAL_NUMBER: string,
  SHIPPING_DATE: string,
  ZSCHEDULE: SerializedQuikZSchedule[],
  ITEMNO: string,
  IMAGES: ImageTypeBucket,
};

export type QuikShipmentDetail = {
  id: string,
  cartonNumber: string,
  materialNumber: string,
  quantity: number,
  uom: string,
  materialDescription: string,
  carrier: string,
  trackingNumber: string,
  trackingUrl: string,
  orderNumber: string,
  orderItem: string,
  oldMaterialNumber: string,
  shippingDate: string,
  zSchedule: QuikZSchedule[],
  itemNo: string,
  images: ImageTypeBucket,
  isExpanded?: boolean,
};

export type SerializedQuikShipmentDetailsResponse = {
  E_SHIPMENT_NUMBER: string,
  E_SHIP_TO_NAME: string,
  E_SHIP_TO_STREET: string,
  E_SHIP_TO_CITY: string,
  E_SHIP_TO_STATE: string,
  E_SHIP_TO_COUNTRY: string,
  E_SHIP_TO_POSTAL_CODE: string,
  E_SHIPPING_DATE: string,
  E_CARRIER: string,
  E_NUMBER_OF_PACKAGES: number,
  E_TOTAL_NET_WEIGHT: number,
  E_WEIGHT_UNIT: string,
  E_NET_WORTH: string,
  E_NET_CURRENCY: string,
  E_STATUS: string,
  E_FREIGHT_CHARGES: string,
  E_FREIGHT_CHARGE_CUR: string,
  ET_SHIPMENT_LIST: SerializedQuikShipmentDetail[],
  ET_RETURN: QuikMetaReturn,
};

export type QuikShipmentDetailsResponse = {
  shipmentNumber: string,
  shipToName: string,
  shipToStreet: string,
  shipToCity: string,
  shipToState: string,
  shipToCountry: string,
  shipToPostalCode: string,
  shippingDate: string,
  carrier: string,
  numberOfPackages: number,
  totalNetWeight: number,
  weightUnit: string,
  netWorth: string,
  netCurrency: string,
  status: string,
  freightCharges: string,
  freightChargeCur: string,
  shipmentList: QuikShipmentDetail[],
};

export type SerializedQuikShipmentDetailsQuery = {
  I_SHIPMENT_NUMBER: string,
  I_LANGUAGE: string,
  I_ORDER_NUMBER: string,
  I_DELIVERY_NUMBER: string,
};

export type QuikShipmentDetailsQuery = {
  shipmentNumber: string,
  orderNumber?: string,
  deliveryNumber?: string,
};

export const serializedQuikShipmentDetailsQuery = ({
  shipmentNumber = '',
  orderNumber = '',
  deliveryNumber = '',
}: QuikShipmentDetailsQuery): SerializedQuikShipmentDetailsQuery => ({
  I_SHIPMENT_NUMBER: shipmentNumber,
  I_LANGUAGE,
  I_ORDER_NUMBER: orderNumber,
  I_DELIVERY_NUMBER: deliveryNumber,
});

export const deserializedQuikShipmentDetailsResponse = (
  response: SerializedQuikShipmentDetailsResponse,
): QuikShipmentDetailsResponse => {
  const {
    E_SHIPMENT_NUMBER: shipmentNumber,
    E_SHIP_TO_NAME: shipToName,
    E_SHIP_TO_STREET: shipToStreet,
    E_SHIP_TO_CITY: shipToCity,
    E_SHIP_TO_STATE: shipToState,
    E_SHIP_TO_COUNTRY: shipToCountry,
    E_SHIP_TO_POSTAL_CODE: shipToPostalCode,
    E_SHIPPING_DATE: shippingDate,
    E_CARRIER: carrier,
    E_NUMBER_OF_PACKAGES: numberOfPackages,
    E_TOTAL_NET_WEIGHT: totalNetWeight,
    E_WEIGHT_UNIT: weightUnit,
    E_NET_WORTH: netWorth,
    E_NET_CURRENCY: netCurrency,
    E_STATUS: status,
    E_FREIGHT_CHARGES: freightCharges,
    E_FREIGHT_CHARGE_CUR: freightChargeCur,
    ET_SHIPMENT_LIST: shipmentList,
  } = response || { ...null };
  return {
    shipmentNumber,
    shipToName,
    shipToStreet,
    shipToCity,
    shipToState,
    shipToCountry,
    shipToPostalCode,
    shippingDate,
    carrier,
    numberOfPackages: Number(numberOfPackages),
    totalNetWeight: Number(totalNetWeight),
    weightUnit,
    netWorth,
    netCurrency,
    status,
    freightCharges,
    freightChargeCur,
    shipmentList: shipmentList.map(
      ({
        CARTON_NUMBER: cartonNumber,
        MATERIAL_NUMBER: materialNumber,
        QUANTITY: quantity,
        UOM: uom,
        MATERIAL_DESCRIPTION: materialDescription,
        CARRIER: carrier,
        TRACKING_NUMBER: trackingNumber,
        TRACKING_URL: trackingUrl,
        ORDER_NUMBER: orderNumber,
        ORDER_ITEM: orderItem,
        OLD_MATERIAL_NUMBER: oldMaterialNumber,
        SHIPPING_DATE: shippingDate,
        ZSCHEDULE: zSchedule,
        ITEMNO: itemNo,
        IMAGES: images,
      }: SerializedQuikShipmentDetail): QuikShipmentDetail => ({
        id: `QuikShipmentDetail:${[
          shipmentNumber,
          cartonNumber,
          orderNumber,
          orderItem,
        ].join('::')}`,
        cartonNumber,
        materialNumber,
        quantity: Number(quantity),
        uom,
        materialDescription,
        carrier,
        trackingNumber,
        trackingUrl,
        orderNumber,
        orderItem,
        oldMaterialNumber,
        shippingDate,
        zSchedule: zSchedule.map(
          ({
            GRID_SIZE: gridSize,
            QUANTITY: quantity,
            UOM: uom,
            UPC: upc,
          }: SerializedQuikZSchedule): QuikZSchedule => ({
            id: `QuikZSchedule:${[
              upc,
              uom,
              orderNumber,
              orderItem,
              cartonNumber,
            ].join('::')}`,
            gridSize,
            quantity: Number(quantity),
            uom,
            upc,
          }),
        ),
        itemNo,
        images,
      }),
    ),
  };
};

// Open Items (as in Invoices)
export type SerializedQuikOpenItem = {
  DOCUMENT_DATE: string,
  DOCUMENT_NUMBER: string,
  DOCUMENT_TYPE: string,
  DOCUMENT_TYPE_DESC: string,
  DUE_DATE: string,
  REFERENCE: string,
  BALANCE: string,
  PO_NUMBER: string,
  DOCUMENT_CURR: string,
};

export type QuikOpenItem = $ReadOnly<{|
  id: string,
  documentDate: string,
  documentNumber: string,
  documentType: string,
  documentTypeDesc: string,
  dueDate: string,
  reference: string,
  balance: string,
  poNumber: string,
  documentCurrency: string,
|}>;

type QuikOpenItemsListResponse = {
  ET_OUTPUT: SerializedQuikOpenItem[],
  ET_RETURN: QuikMetaReturn,
};

export type SerializedQuikOpenItemsListQuery = {
  I_LANGUAGE: string,
  I_CUSTOMER_NUMBER: string,
  I_PO_NUMBER: string,
  I_DOCUMENT_NUMBER: string,
  I_DOCUMENT_TYPE_T: string[],
  I_DOCUMENT_DATE_FROM: string,
  I_DOCUMENT_DATE_TO: string,
  I_DUE_DATE_FROM: string,
  I_DUE_DATE_TO: string,
  I_ORDER_NUMBER: string,
  I_DELIVERY_NUMBER: string,
  IT_BRANDS: $ReadOnlyArray<{| SPART: string |}>,
};

export type QuikOpenItemsListQuery = {
  customerNumber: string,
  poNumber?: string,
  documentNumber?: string,
  documentTypeT?: string[],
  documentDateFrom?: Date | null,
  documentDateTo?: Date | null,
  dueDateFrom?: Date | null,
  dueDateTo?: Date | null,
  orderNumber?: string,
  deliveryNumber?: string,
  brand:
    | $ReadOnlyArray<{| label: string, value: string |}>
    | $ReadOnly<{| label: string, value: string |}>,
  allBrands: $ReadOnlyArray<{| label: string, value: string |}>,
  invoiceType: string,
};

export const serializedQuikOpenItemsListQuery = ({
  customerNumber = '',
  poNumber = '',
  documentNumber = '',
  documentTypeT = [],
  documentDateFrom = null,
  documentDateTo = null,
  dueDateFrom = null,
  dueDateTo = null,
  orderNumber = '',
  deliveryNumber = '',
  brand = [],
  invoiceType = '',
}: QuikOpenItemsListQuery): SerializedQuikOpenItemsListQuery => ({
  I_LANGUAGE,
  I_CUSTOMER_NUMBER: customerNumber,
  I_PO_NUMBER: poNumber,
  I_DOCUMENT_NUMBER: documentNumber,
  I_DOCUMENT_TYPE_T: documentTypeT,
  I_DOCUMENT_DATE_FROM: maybeQuikDate(documentDateFrom),
  I_DOCUMENT_DATE_TO: maybeQuikDate(documentDateTo),
  I_DUE_DATE_FROM: maybeQuikDate(dueDateFrom),
  I_DUE_DATE_TO: maybeQuikDate(dueDateTo),
  I_ORDER_NUMBER: orderNumber,
  I_DELIVERY_NUMBER: deliveryNumber,
  I_INVOICE_FLAG: invoiceType,
  IT_BRANDS: Array.isArray(brand)
    ? brand.map((b) => ({ SPART: b.value }))
    : [{ SPART: brand.value }],
});

export const deserializedQuikOpenItemsListResponse = (
  response: QuikOpenItemsListResponse,
): Array<any> => {
  // TODO: this pattern is totally broken and messing with the typing below,
  // which is why the FlowFixMe is necessary. make this stuff make sense, and
  // the type error will dry up.
  const { ET_OUTPUT: openItemsList = [] } = response || { ...null };
  return openItemsList.map(
    ({
      DOCUMENT_DATE,
      DOCUMENT_NUMBER,
      DOCUMENT_TYPE,
      DOCUMENT_TYPE_DESC,
      DUE_DATE,
      REFERENCE,
      BALANCE,
      PO_NUMBER,
      DOCUMENT_CURR,
    }: SerializedQuikOpenItem): QuikOpenItem =>
      ({
        id: `QuikOpenItem:${[
          DOCUMENT_NUMBER,
          DOCUMENT_TYPE,
          PO_NUMBER,
          DOCUMENT_DATE,
        ].join('::')}`,
        documentDate: DOCUMENT_DATE,
        documentNumber: DOCUMENT_NUMBER,
        documentType: DOCUMENT_TYPE,
        documentTypeDesc: DOCUMENT_TYPE_DESC,
        dueDate: DUE_DATE,
        reference: REFERENCE,
        balance: BALANCE,
        poNumber: PO_NUMBER,
        documentCurrency: DOCUMENT_CURR,
      }: QuikOpenItem),
  );
};

// Payments
export type SerializedQuikPayment = {
  POSTING_DATE: string,
  PAYMENT_REFERENCE: string,
  AMOUNT: string,
  CURRENCY: string,
};

export type QuikPayment = $ReadOnly<{|
  id: string,
  postingDate: string,
  paymentReference: string,
  amount: string,
  currency: string,
  postingDate: string,
  paymentReference: string,
  amount: string,
  currency: string,
|}>;

type QuikPaymentListResponse = {
  ET_OUTPUT: SerializedQuikPayment[],
  ET_RETURN: QuikMetaReturn,
};

export type SerializedQuikPaymentListQuery = {
  I_CUSTOMER: string,
  I_POSTING_DATE_FROM: string,
  I_POSTING_DATE_TO: string,
  I_PAYMENT_REFERENCE: string,
};

export type QuikPaymentListQuery = {
  customerNumber: string,
  postingDateFrom?: Date | null,
  postingDateTo?: Date | null,
  paymentReference?: string,
};

export const serializedQuikPaymentListQuery = ({
  customerNumber = '',
  postingDateFrom = null,
  postingDateTo = null,
  paymentReference = '',
}: QuikPaymentListQuery): SerializedQuikPaymentListQuery => ({
  I_LANGUAGE,
  I_CUSTOMER: customerNumber,
  I_POSTING_DATE_FROM: maybeQuikDate(postingDateFrom),
  I_POSTING_DATE_TO: maybeQuikDate(postingDateTo),
  I_PAYMENT_REFERENCE: paymentReference,
});

export const deserializedQuikPaymentListResponse = (
  response: QuikPaymentListResponse,
): Array<any> => {
  // TODO: this pattern is totally broken and messing with the typing below,
  // which is why the FlowFixMe is necessary. make this stuff make sense, and
  // the type error will dry up.
  const { ET_OUTPUT: paymentList = [] } = response || { ...null };
  return paymentList.map(
    ({
      POSTING_DATE,
      PAYMENT_REFERENCE,
      AMOUNT,
      CURRENCY,
    }: SerializedQuikPayment): QuikPayment =>
      ({
        id: `QUIK_PAYMENT:${PAYMENT_REFERENCE}::${POSTING_DATE}`,
        postingDate: POSTING_DATE,
        paymentReference: PAYMENT_REFERENCE,
        amount: AMOUNT,
        currency: CURRENCY,
      }: QuikPayment),
  );
};

// Payment Details
export type SerializedQuikPaymentDetails = {
  CUSTOMER_NUMBER: string,
  CUSTOMER_NAME: string,
  CUSTOMER_CITY: string,
  CLEAR_DOC: string,
  CHECK_NUMBER: string,
  AMOUNT: string,
  CURRENCY: string,
  POSTING_DATE: string,
  DOC_DATE: string,
  NET_DUE_DATE: string,
  DOCUMENT_NUMBER: string,
  DOCUMENT_TYPE: string,
  DOCUMENT_TYPE_DESC: string,
  PAYMENT_REFERENCE: string,
  DAYS_LATE: number,
};

export type QuikPaymentDetails = {
  customerNumber: string,
  customerName: string,
  customerCity: string,
  clearDoc: string,
  checkNumber: string,
  amount: string,
  currency: string,
  postingDate: string,
  docDate: string,
  netDueDate: string,
  documentNumber: string,
  documentType: string,
  documentTypeDesc: string,
  paymentReference: string,
  daysLate: number,
};

type QuikPaymentDetailsResponse = {
  ET_OUTPUT: SerializedQuikPaymentDetails,
  ET_RETURN: QuikMetaReturn,
};

export type SerializedQuikPaymentDetailsQuery = {
  I_LANGUAGE: string,
  I_COMPANY_CODE: string,
  I_CUSTOMER: string,
  I_POSTING_DATE_FROM: string,
  I_POSTING_DATE_TO: string,
  I_PAYMENT_REFERENCE: string,
};

export type QuikPaymentDetailsQuery = {
  companyCode?: string,
  customer: string,
  postingDateFrom?: string,
  postingDateTo?: string,
  paymentReference: string,
};

export const serializedQuikPaymentDetailsQuery = ({
  companyCode = '',
  customer = '',
  postingDateFrom = '',
  postingDateTo = '',
  paymentReference = '',
}: QuikPaymentDetailsQuery): SerializedQuikPaymentDetailsQuery => ({
  I_LANGUAGE,
  I_COMPANY_CODE: companyCode,
  I_CUSTOMER: customer,
  I_POSTING_DATE_FROM: postingDateFrom,
  I_POSTING_DATE_TO: postingDateTo,
  I_PAYMENT_REFERENCE: paymentReference,
});

export const deserializedQuikPaymentDetailsResponse = (
  response: QuikPaymentDetailsResponse,
): any | Array<any> => {
  // TODO: this pattern is totally broken and messing with the typing below,
  // which is why the FlowFixMe is necessary. make this stuff make sense, and
  // the type error will dry up.
  const { ET_OUTPUT: paymentDetailsList = [] } = response || { ...null };
  return (
    paymentDetailsList
      // $FlowFixMe[prop-missing]
      .map(
        ({
          CUSTOMER_NUMBER: customerNumber,
          CUSTOMER_NAME: customerName,
          CUSTOMER_CITY: customerCity,
          CLEAR_DOC: clearDoc,
          CHECK_NUMBER: checkNumber,
          AMOUNT: amount,
          CURRENCY: currency,
          POSTING_DATE: postingDate,
          DOC_DATE: docDate,
          NET_DUE_DATE: netDueDate,
          DOCUMENT_NUMBER: documentNumber,
          DOCUMENT_TYPE: documentType,
          DOCUMENT_TYPE_DESC: documentTypeDesc,
          PAYMENT_REFERENCE: paymentReference,
          DAYS_LATE: daysLate,
        }: SerializedQuikPaymentDetails): QuikPaymentDetails =>
          ({
            customerNumber,
            customerName,
            customerCity,
            clearDoc,
            checkNumber,
            amount,
            currency,
            postingDate,
            docDate,
            netDueDate,
            documentNumber,
            documentType,
            documentTypeDesc,
            paymentReference,
            daysLate: Number(daysLate),
          }: QuikPaymentDetails),
      )
  );
};

// Order Details
type SerializedQuikOrderItem = {
  ITEM_NUMBER: string,
  MATERIAL_NUMBER: string,
  MATERIAL_DESCRIPTION: string,
  MATERIAL_GROUP_NAME: string,
  MATERIAL_GROUP_DESC: string,
  SELLING_GROUP_VALUE: string,
  SELLING_GROUP_NAME: string,
  MATERIAL_COLLECTION_VALUE: string,
  MATERIAL_COLLECTION_NAME: string,
  SEGMENT_VALUE: string,
  SEGMENT_NAME: string,
  MARKET_CLASSIFICATION_VALUE: string,
  MARKET_CLASSIFICATION_NAME: string,
  ZSCHEDULE: SerializedQuikZSchedule[],
  LINE_QUANTITY: string,
  UOM: string,
  BASE_PRICE: string,
  BASE_CURRENCY: string,
  TOTAL_BASE_PRICE: string,
  TOTAL_BASE_CURRENCY: string,
  MSRP: string,
  MSRP_CURRENCY: string,
  MAP: string,
  MAP_CURRENCY: string,
  DISCOUNT: string,
  DISCOUNT_CURRENCY: string,
  UNIT_NET_PRICE: string,
  UNIT_NET_CURRENCY: string,
  NET_PRICE: string,
  NET_CURRENCY: string,
  REFERENCE_DOCUMENT: string,
  REFERENCE_DOCUMENT_ITEM: string,
  STATUS: string,
  REQ_DEL_DATE: string,
  DIM1: string,
  IMAGES: ImageTypeBucket,
};

export type QuikOrderItem = {
  itemNumber: string,
  materialNumber: string,
  materialDescription: string,
  materialGroupName: string,
  materialGroupDesc: string,
  sellingGroupValue: string,
  sellingGroupName: string,
  materialCollectionValue: string,
  materialCollectionName: string,
  segmentValue: string,
  segmentName: string,
  marketClassificationValue: string,
  marketClassificationName: string,
  zSchedule: QuikZSchedule[],
  lineQuantity: number,
  uom: string,
  basePrice: string,
  baseCurrency: string,
  totalBasePrice: string,
  totalBaseCurrency: string,
  msrp: string,
  msrpCurrency: string,
  map: string,
  mapCurrency: string,
  discount: string,
  discountCurrency: string,
  unitNetPrice: string,
  unitNetCurrency: string,
  netPrice: string,
  netCurrency: string,
  referenceDocument: string,
  referenceDocumentItem: string,
  status: string,
  reqDelDate: string,
  dim1: string,
  images: ImageTypeBucket,
  isExpanded?: boolean,
};

export type SerializedQuikOrderDetails = {
  E_SDOC_NUMBER: string,
  E_PO_NUMBER: string,
  E_SDOC_TYPE: string,
  E_SDOC_TYPE_DESC: string,
  E_SDOC_STATUS: string,
  E_CREATED_BY: string,
  E_CREATION_DATE: string,
  E_RDD: string,
  E_CANCEL_DATE: string,
  E_CUSTOMER_NAME: string,
  E_SHIP_TO_NAME: string,
  E_SHIP_TO_STREET: string,
  E_SHIP_TO_CITY: string,
  E_SHIP_TO_STATE: string,
  E_SHIP_TO_COUNTRY: string,
  E_SHIP_TO_POSTAL_CODE: string,
  E_SDOC_QUANTITY: string,
  E_SDOC_NET_VALUE: string,
  E_SDOC_NET_PRICE: string,
  E_SDOC_TAX_AMOUNT: string,
  E_SDOC_TOT_PRICE: string,
  E_SDOC_TOT_BASE_PRICE: string,
  E_SDOC_CURRENCY: string,
  E_SDOC_NET_WEIGHT: string,
  E_SDOC_NET_WEIGHT_UOM: string,
  E_PAYMENT_TERMS: string,
  E_PAYMENT_TERMS_DESC: string,
  E_ORDER_SOURCE: string,
  E_ORD_REASON_DESC: string,
  E_SDOC_TOT_FREIGHT: string,
  ET_ORDER_ITEMS: SerializedQuikOrderItem[],
};

export type QuikOrderDetails = {
  sdocNumber: string,
  poNumber: string,
  sdocType: string,
  sdocTypeDesc: string,
  sdocStatus: string,
  createdBy: string,
  creationDate: string,
  rdd: string,
  cancelDate: string,
  customerName: string,
  shipToName: string,
  shipToStreet: string,
  shipToCity: string,
  shipToState: string,
  shipToCountry: string,
  shipToPostalCode: string,
  sdocQuantity: number,
  sdocNetValue: string,
  sdocNetPrice: string,
  sdocTaxAmount: string,
  sdocTotPrice: string,
  sdocTotBasePrice: string,
  sdocCurrency: string,
  sdocNetWeight: string,
  sdocNetWeightUom: string,
  paymentTerms: string,
  paymentTermsDesc: string,
  orderSource: string,
  ordReasonDesc: string,
  sdocTotFreight: string,
  orderItems: QuikOrderItem[],
};

type QuikOrderDetailsResponse = {
  E_SDOC_NUMBER: string,
  E_PO_NUMBER: string,
  E_SDOC_TYPE: string,
  E_SDOC_TYPE_DESC: string,
  E_SDOC_STATUS: string,
  E_CREATED_BY: string,
  E_CREATION_DATE: string,
  E_RDD: string,
  E_CANCEL_DATE: string,
  E_CUSTOMER_NAME: string,
  E_SHIP_TO_NAME: string,
  E_SHIP_TO_STREET: string,
  E_SHIP_TO_CITY: string,
  E_SHIP_TO_STATE: string,
  E_SHIP_TO_COUNTRY: string,
  E_SHIP_TO_POSTAL_CODE: string,
  E_SDOC_QUANTITY: string,
  E_SDOC_NET_VALUE: string,
  E_SDOC_NET_PRICE: string,
  E_SDOC_TAX_AMOUNT: string,
  E_SDOC_TOT_PRICE: string,
  E_SDOC_TOT_BASE_PRICE: string,
  E_SDOC_CURRENCY: string,
  E_SDOC_NET_WEIGHT: string,
  E_SDOC_NET_WEIGHT_UOM: string,
  E_PAYMENT_TERMS: string,
  E_PAYMENT_TERMS_DESC: string,
  E_ORDER_SOURCE: string,
  E_ORD_REASON_DESC: string,
  E_SDOC_TOT_FREIGHT: string,
  ET_ORDER_ITEMS: SerializedQuikOrderItem[],
  ET_RETURN: QuikMetaReturn[],
};

export type SerializedQuikOrderDetailsQuery = {
  I_SDOC_NUMBER: string,
  I_VBTYP: string,
  I_CUSTOMER_VIEW_FLAG: string,
};

export type QuikOrderDetailsQuery = {
  sdocNumber: string,
  vbtyp?: string,
  customerViewFlag?: string,
};

export const serializedQuikOrderDetailsQuery = ({
  sdocNumber = '',
  vbtyp = '',
  customerViewFlag = '',
}: QuikOrderDetailsQuery): SerializedQuikOrderDetailsQuery => ({
  I_SDOC_NUMBER: sdocNumber,
  I_VBTYP: vbtyp,
  I_LANGUAGE,
  I_CUSTOMER_VIEW_FLAG: customerViewFlag,
});

export const deserializedQuikOrderDetailsResponse = (
  response: QuikOrderDetailsResponse,
): QuikOrderDetails => {
  const {
    E_SDOC_NUMBER: sdocNumber,
    E_PO_NUMBER: poNumber,
    E_SDOC_TYPE: sdocType,
    E_SDOC_TYPE_DESC: sdocTypeDesc,
    E_SDOC_STATUS: sdocStatus,
    E_CREATED_BY: createdBy,
    E_CREATION_DATE: creationDate,
    E_RDD: rdd,
    E_CANCEL_DATE: cancelDate,
    E_CUSTOMER_NAME: customerName,
    E_SHIP_TO_NAME: shipToName,
    E_SHIP_TO_STREET: shipToStreet,
    E_SHIP_TO_CITY: shipToCity,
    E_SHIP_TO_STATE: shipToState,
    E_SHIP_TO_COUNTRY: shipToCountry,
    E_SHIP_TO_POSTAL_CODE: shipToPostalCode,
    E_SDOC_QUANTITY: sdocQuantity,
    E_SDOC_NET_VALUE: sdocNetValue,
    E_SDOC_NET_PRICE: sdocNetPrice,
    E_SDOC_TAX_AMOUNT: sdocTaxAmount,
    E_SDOC_TOT_PRICE: sdocTotPrice,
    E_SDOC_TOT_BASE_PRICE: sdocTotBasePrice,
    E_SDOC_CURRENCY: sdocCurrency,
    E_SDOC_NET_WEIGHT: sdocNetWeight,
    E_SDOC_NET_WEIGHT_UOM: sdocNetWeightUom,
    E_PAYMENT_TERMS: paymentTerms,
    E_PAYMENT_TERMS_DESC: paymentTermsDesc,
    E_ORDER_SOURCE: orderSource,
    E_ORD_REASON_DESC: ordReasonDesc,
    E_SDOC_TOT_FREIGHT: sdocTotFreight,
    ET_ORDER_ITEMS: orderItems,
  } = response || { ...null };
  return ({
    sdocNumber,
    poNumber,
    sdocType,
    sdocTypeDesc,
    sdocStatus,
    createdBy,
    creationDate,
    rdd,
    cancelDate,
    customerName,
    shipToName,
    shipToStreet,
    shipToCity,
    shipToState,
    shipToCountry,
    shipToPostalCode,
    sdocQuantity: Number(sdocQuantity),
    sdocNetValue,
    sdocNetPrice,
    sdocTaxAmount,
    sdocTotPrice,
    sdocTotBasePrice,
    sdocCurrency,
    sdocNetWeight,
    sdocNetWeightUom,
    paymentTerms,
    paymentTermsDesc,
    orderSource,
    ordReasonDesc,
    sdocTotFreight,
    orderItems: orderItems.map(
      ({
        ITEM_NUMBER,
        MATERIAL_NUMBER,
        MATERIAL_DESCRIPTION,
        MATERIAL_GROUP_NAME,
        MATERIAL_GROUP_DESC,
        SELLING_GROUP_VALUE,
        SELLING_GROUP_NAME,
        MATERIAL_COLLECTION_VALUE,
        MATERIAL_COLLECTION_NAME,
        SEGMENT_VALUE,
        SEGMENT_NAME,
        MARKET_CLASSIFICATION_VALUE,
        MARKET_CLASSIFICATION_NAME,
        ZSCHEDULE,
        LINE_QUANTITY,
        UOM,
        BASE_PRICE,
        BASE_CURRENCY,
        TOTAL_BASE_PRICE,
        TOTAL_BASE_CURRENCY,
        MSRP,
        MSRP_CURRENCY,
        MAP,
        MAP_CURRENCY,
        DISCOUNT,
        DISCOUNT_CURRENCY,
        UNIT_NET_PRICE,
        UNIT_NET_CURRENCY,
        NET_PRICE,
        NET_CURRENCY,
        REFERENCE_DOCUMENT,
        REFERENCE_DOCUMENT_ITEM,
        STATUS,
        REQ_DEL_DATE,
        DIM1,
        IMAGES,
      }): QuikOrderItem => ({
        itemNumber: ITEM_NUMBER,
        materialNumber: MATERIAL_NUMBER,
        materialDescription: MATERIAL_DESCRIPTION,
        materialGroupName: MATERIAL_GROUP_NAME,
        materialGroupDesc: MATERIAL_GROUP_DESC,
        sellingGroupValue: SELLING_GROUP_VALUE,
        sellingGroupName: SELLING_GROUP_NAME,
        materialCollectionValue: MATERIAL_COLLECTION_VALUE,
        materialCollectionName: MATERIAL_COLLECTION_NAME,
        segmentValue: SEGMENT_VALUE,
        segmentName: SEGMENT_NAME,
        marketClassificationValue: MARKET_CLASSIFICATION_VALUE,
        marketClassificationName: MARKET_CLASSIFICATION_NAME,
        zSchedule: ZSCHEDULE.map(
          ({
            SCHEDULE_LINE,
            GRID_SIZE,
            QUANTITY,
            OPEN_QUANTITY,
            ALLOCATED_QUANTITY,
            IN_PROCESS_QUANTITY,
            INVOICED_QUANTITY,
            REJECTED_QUANTITY,
            DRAWN_QUANTITY,
            REMAINING_QUANTITY,
            UOM,
            CONFIRMED_DATE,
            STATUS,
            SHIPPED_QUANTITY,
            DELIVERED_QUANTITY,
            UPC,
          }): QuikZSchedule => ({
            id: `QuikZSchedule:${[
              UPC,
              UOM,
              sdocNumber,
              poNumber,
              ITEM_NUMBER,
            ].join('::')}`,
            scheduleLine: SCHEDULE_LINE,
            gridSize: GRID_SIZE,
            quantity: Number(QUANTITY),
            openQuantity: Number(OPEN_QUANTITY),
            allocatedQuantity: Number(ALLOCATED_QUANTITY),
            inProcessQuantity: Number(IN_PROCESS_QUANTITY),
            invoicedQuantity: Number(INVOICED_QUANTITY),
            rejectedQuantity: Number(REJECTED_QUANTITY),
            drawnQuantity: Number(DRAWN_QUANTITY),
            remainingQuantity: Number(REMAINING_QUANTITY),
            uom: UOM,
            confirmedDate: CONFIRMED_DATE,
            status: STATUS,
            shippedQuantity: Number(SHIPPED_QUANTITY),
            deliveredQuantity: Number(DELIVERED_QUANTITY),
            upc: UPC,
          }),
        ),
        lineQuantity: Number(LINE_QUANTITY),
        uom: UOM,
        basePrice: BASE_PRICE,
        baseCurrency: BASE_CURRENCY,
        totalBasePrice: TOTAL_BASE_PRICE,
        totalBaseCurrency: TOTAL_BASE_CURRENCY,
        msrp: MSRP,
        msrpCurrency: MSRP_CURRENCY,
        map: MAP,
        mapCurrency: MAP_CURRENCY,
        discount: DISCOUNT,
        discountCurrency: DISCOUNT_CURRENCY,
        unitNetPrice: UNIT_NET_PRICE,
        unitNetCurrency: UNIT_NET_CURRENCY,
        netPrice: NET_PRICE,
        netCurrency: NET_CURRENCY,
        referenceDocument: REFERENCE_DOCUMENT,
        referenceDocumentItem: REFERENCE_DOCUMENT_ITEM,
        status: STATUS,
        reqDelDate: REQ_DEL_DATE,
        dim1: DIM1,
        images: IMAGES,
      }),
    ),
  }: QuikOrderDetails);
};

export type Brand = $ReadOnly<{|
  key: string,
  division: string,
  name: string,
  display: string,
|}>;

// https://github.com/ElasticSuite/quiksilver-spice/blob/00d3c67f80ff6eaa9bf09eab0aca35aed8e194c9/import/helpers/brand_helpers.rb#L2-L15
export const brandOptions: $ReadOnlyArray<Brand> = [
  {
    key: 'dcshoes',
    division: '01',
    name: 'DC Shoes',
    display: '01 - DC Shoes',
  },
  {
    key: 'quiksilver',
    division: '02',
    name: 'Quiksilver',
    display: '02 - Quiksilver',
  },
  {
    key: 'roxy',
    division: '03',
    name: 'Roxy',
    display: '03 - Roxy',
  },
  {
    key: 'billabong',
    division: '20',
    name: 'Billabong',
    display: '20 - Billabong',
  },
  {
    key: 'element',
    division: '21',
    name: 'Element',
    display: '21 - Element',
  },
  {
    key: 'rvca',
    division: '22',
    name: 'RVCA',
    display: '22 - RVCA',
  },
  {
    key: 'vonzipper',
    division: '23',
    name: 'VonZipper',
    display: '23 - VonZipper',
  },
  {
    key: 'kustom',
    division: '24',
    name: 'Kustom',
    display: '24 - Kustom',
  },
  {
    key: 'xcel',
    division: '25',
    name: 'XCEL',
    display: '25 - XCEL',
  },
  {
    key: 'honolua',
    division: '26',
    name: 'Honolua',
    display: '26 - Honolua',
  },
  {
    key: 'dotdash',
    division: '27',
    name: 'Dot Dash',
    display: '27 - Dot Dash',
  },
  {
    key: 'palmers',
    division: '28',
    name: 'Palmers',
    display: '28 - Palmers',
  },
  {
    key: 'thirdparty',
    division: '99',
    name: 'ThirdParty',
    display: '99 - Third Party Brands',
  },
];

export const QUIK_CATALOG_PERMISSIONS_FORMAT: RegExp =
  /\d{4}-\d{2}-(\d{2})-\d{4}/;

export const customerBrandOptions = (
  user: User,
  customerNumber: string,
): $ReadOnlyArray<{| label: string, value: string |}> => {
  const entries = (user.customer.catalog_permissions || { ...null })[
    customerNumber
  ];

  if (!entries) return [];

  return entries
    .map((entry) => entry.match(QUIK_CATALOG_PERMISSIONS_FORMAT))
    .filter(Boolean)
    .map((match) => match[1])
    .filter(Boolean)
    .map((division) => brandOptions.find((b) => b.division === division))
    .filter(Boolean)
    .map(({ display: label, division: value }) => ({ label, value }));
};

export const customerSellingGroupOptions = (
  user: User,
): $ReadOnlyArray<{| label: string, value: string |}> =>
  sortBy(
    (tag) => tag.label,
    uniqBy(
      (s) => s.value,
      catalogsForCustomer(user, user.customer, user.catalogs)
        .flatMap(
          (item) =>
            item.tags &&
            item.tags['Selling Group'] &&
            flatMap(
              (tag): $ReadOnlyArray<$ReadOnlyArray<string>> =>
                item?.client_fields?.selling_groups_with_number
                  ? item?.client_fields?.selling_groups_with_number?.map((sg) =>
                      typeof tag === 'string' && sg.match(tag)
                        ? sg.split(' - ')
                        : [],
                    )
                  : [],
              item.tags['Selling Group'],
            ),
        )
        .filter((e) => e && e.length === 2)
        .filter(Boolean)
        .map((selGroup) => ({
          label: selGroup[0],
          value: selGroup[1],
        })),
    ),
  );

// takes an array of OrderItem Images and returns the urls
export const orderImagesMap = (
  orderItemImages: void | Array<ImageRecord>,
  userOptions: DefaultOptions,
  isAlternate: boolean,
): $ReadOnlyArray<string> =>
  orderItemImages
    ? orderItemImages
        .flatMap((images) =>
          Object.entries(images).map((img) =>
            (isAlternate &&
              userOptions.alternate === true &&
              img[0] === 'original') ||
            (userOptions[img[0]] !== false && !isAlternate)
              ? String(img[1])
              : '',
          ),
        )
        .filter(identity) // filter to remove empty elements
    : [];

// takes orderDetails and returns a list of links for all images
export const generateLinksFromQuikOrderDetails = (
  orderDetails: QuikOrderDetails,
  userOptions: DefaultOptions,
  alternateImages: boolean,
): $ReadOnlyArray<string> => {
  const imageLinks = orderDetails.orderItems.flatMap((orderItem) => [
    ...(!alternateImages
      ? orderImagesMap(orderItem.images.P, userOptions, false)
      : []),
    ...(userOptions.alternate && alternateImages
      ? orderImagesMap(orderItem.images.A, userOptions, true)
      : []),
    ...(!alternateImages
      ? orderImagesMap(orderItem.images.S, userOptions, false)
      : []),
  ]);
  return imageLinks;
};

// generates a folder name and a file name for each image link
export const imageFileAndFolderNameFromLink = (
  link: string,
  isAlternate: boolean,
): [string, string] => {
  const parts = link.split('/');
  const end = parts[parts.length - 1];

  // eslint-disable-next-line no-nested-ternary
  const folderName = !isAlternate
    ? parts[parts.length - 2] === 'orignal'
      ? 'high-res'
      : parts[parts.length - 2]
    : 'alternate';
  return [folderName, end.split('?')[0]];
};

// facilitator for downloading images from quik's order details
export const downloadImagesFromQuikOrderDetails = (
  orderDetails: QuikOrderDetails,
  onProgress: (total: number, completed: number) => void,
  userOptions: DefaultOptions,
): BluebirdPromise<string> =>
  new BluebirdPromise((resolve, reject, onCancel) => {
    // get only original image links
    const nonAlternatelinks = generateLinksFromQuikOrderDetails(
      orderDetails,
      userOptions,
      false,
    );

    // get only alternate images because there is not way to know which
    // image is alternate from the path only in imageFileAndFolderNameFromLink
    const alternateLinks = generateLinksFromQuikOrderDetails(
      orderDetails,
      userOptions,
      true,
    );

    const names = [
      ...nonAlternatelinks.map((link) =>
        imageFileAndFolderNameFromLink(link, false),
      ),
      ...alternateLinks.map((link) =>
        imageFileAndFolderNameFromLink(link, true),
      ),
    ];

    // we have know the links and the proper path lets concat all links
    const links = [...nonAlternatelinks, ...alternateLinks];

    const retreiver = retrieveFilesFromUrls(links, onProgress)
      .then((resp) =>
        zipArchive(resp, names).then((resp) => {
          downloadFile(resp, `${orderDetails.sdocNumber} Media`);
          resolve('success');
        }),
      )
      .catch(reject);

    if (onCancel) onCancel(() => retreiver.cancel());
  });
